import React, { useEffect, useState } from 'react';

// Modal «Cerrar trimestre»: antes de cerrar enseña en qué trimestre están las
// facturas y en cuál van a quedar, y deja cambiarlo. Viene relleno con el
// trimestre donde están; si se elige otro, avisa de a dónde pasan. Cuando lo
// que se cierra está en más de un trimestre (marcadas desde la vista «Año»),
// sale una línea por trimestre.
//
// Copia el armazón de los modales de Salientes (modal-overlay + modal) y los
// campos de «Reabrir un trimestre cerrado» de Ajustes (desplegable de
// trimestre y casilla de año), con sus mismos límites de año.
//
// Props:
//   grupos    : [{ year, quarter, cuantas }] — dónde están las que se cierran.
//   documento : { uno, varios } — «factura» / «facturas», «proforma» / «proformas».
//   nota      : frase sobre si el cierre se puede deshacer.
//   ocupado   : true mientras corre el cierre (todo queda inactivo).
//   onCancel  : () => void
//   onConfirm : (destinos) => void — destinos[i] = { year, quarter } es el
//               trimestre en el que queda grupos[i].
const TRIMESTRES = [
  { value: 1, label: 'T1 (ene-mar)' },
  { value: 2, label: 'T2 (abr-jun)' },
  { value: 3, label: 'T3 (jul-sep)' },
  { value: 4, label: 'T4 (oct-dic)' },
];

const anioValido = (texto) => {
  if (!/^\d+$/.test(String(texto).trim())) return null;
  const y = parseInt(texto, 10);
  return y >= 2000 && y <= 2100 ? y : null;
};

const CierreTrimestreModal = ({ grupos, documento, nota, ocupado = false, onCancel, onConfirm }) => {
  const [destinos, setDestinos] = useState(() => grupos.map((g) => ({ year: String(g.year), quarter: g.quarter })));

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !ocupado) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ocupado, onCancel]);

  const total = grupos.reduce((suma, g) => suma + g.cuantas, 0);
  const nombre = (n) => (n === 1 ? documento.uno : documento.varios);
  const cambiar = (i, campo, valor) => setDestinos((prev) => prev.map((d, j) => (j === i ? { ...d, [campo]: valor } : d)));
  const hayAnioMalo = destinos.some((d) => anioValido(d.year) === null);

  const confirmar = () => {
    if (ocupado || hayAnioMalo) return;
    onConfirm(destinos.map((d) => ({ year: anioValido(d.year), quarter: d.quarter })));
  };

  return (
    <div className="modal-overlay" onClick={() => { if (!ocupado) onCancel(); }}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <h2>Cerrar trimestre</h2>
          <button type="button" className="btn-icon" onClick={onCancel} disabled={ocupado} aria-label="Cerrar sin hacer nada">&times;</button>
        </div>
        <div className="modal-body">
          <p style={{ marginBottom: 14 }}>
            Vas a cerrar {total} {nombre(total)}. Comprueba en qué trimestre van a quedar; puedes cambiarlo.
          </p>
          {grupos.map((g, i) => {
            const d = destinos[i];
            const anio = anioValido(d.year);
            const cambia = anio !== null && (anio !== g.year || d.quarter !== g.quarter);
            return (
              <div
                key={`${g.year}-${g.quarter}`}
                style={{ display: 'flex', flexWrap: 'wrap', columnGap: 24, rowGap: 10, alignItems: 'flex-start', padding: '16px 0', borderTop: '1px solid var(--gray-200)' }}
              >
                <div style={{ flex: '0 0 150px' }}>
                  <div className="form-label">Están en</div>
                  <div style={{ fontWeight: 600, fontSize: '1.05rem', lineHeight: '42px' }}>T{g.quarter} de {g.year}</div>
                  <div style={{ color: 'var(--gray-500)', fontSize: '0.85rem', marginTop: -6 }}>{g.cuantas} {nombre(g.cuantas)}</div>
                </div>
                <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                  <div className="form-label">Quedarán en</div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <select
                      className="form-input"
                      aria-label={`Trimestre en el que quedarán las del T${g.quarter} de ${g.year}`}
                      style={{ flex: '1 1 170px', minWidth: 0 }}
                      value={d.quarter}
                      onChange={(e) => cambiar(i, 'quarter', parseInt(e.target.value, 10))}
                      disabled={ocupado}
                    >
                      {TRIMESTRES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <input
                      className="form-input"
                      type="number"
                      aria-label={`Año en el que quedarán las del T${g.quarter} de ${g.year}`}
                      style={{ flex: '0 0 110px' }}
                      value={d.year}
                      onChange={(e) => cambiar(i, 'year', e.target.value)}
                      disabled={ocupado}
                    />
                  </div>
                </div>
                {cambia && (
                  <div style={{ flex: '1 1 100%', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <span className="badge badge-warning" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>Cambian de trimestre</span>
                    <span style={{ fontSize: '0.9rem', color: 'var(--gray-700)' }}>
                      Pasarán del T{g.quarter} de {g.year} al T{d.quarter} de {anio} y dejarán de verse en {anio === g.year ? `la pestaña T${g.quarter}` : `el T${g.quarter} de ${g.year}`}.
                    </span>
                  </div>
                )}
                {anio === null && (
                  <div style={{ flex: '1 1 100%', fontSize: '0.9rem', color: 'var(--danger)' }}>
                    Indica un año válido.
                  </div>
                )}
              </div>
            );
          })}
          <p style={{ marginTop: 14, color: 'var(--gray-600)', fontSize: '0.9rem', borderTop: '1px solid var(--gray-200)', paddingTop: 14 }}>
            {nota}
          </p>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline" onClick={onCancel} disabled={ocupado}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={confirmar} disabled={ocupado || hayAnioMalo}>
            {ocupado ? 'Cerrando…' : `Cerrar ${total} ${nombre(total)}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CierreTrimestreModal;
